import { Box, FlexLayout, H4 } from '@deity/falcon-ui';
import type { IPropertyBag } from '@deity-io/di-data-basics';
import { mapRecordToArray } from '@deity-io/di-data-basics';
import { FlowLoader } from '@deity-io/di-data-flows';
import { Button, Divider, Icon } from '@deity-io/portal-ui';
import FoldedPanel from 'components/DropInPanel/FoldedPanel';
import { FlowActionTypes, FlowContext } from 'contexts/FlowContext';
import { useContext } from 'react';
import type { FC } from 'react';
import AddFlowComponentPanel from './AddFlowPanel/AddFlowComponentPanel';
import FlowComponentSettings from './FlowComponentSettings/FlowComponentSettings';
import FlowErrorsPanel from './FlowErrors/FlowErrors';
import { FlowSettings } from './FlowSettings/FlowSettings';
import JSONPreview from './JSONPreview/JSONPreview';
import PortSchema from './PortSchema/PortSchema';

interface FlowPanelProps {
  isEditable: boolean;
  panelSettings?: Record<string, IPropertyBag>;
}
const FlowPanel: FC<FlowPanelProps> = ({ isEditable, panelSettings }) => {
  const ctx = useContext(FlowContext);

  const closePanel = () => {
    ctx?.dispatch({ type: FlowActionTypes.TOGGLE_PANEL, payload: null });
  };

  const openPanel = panel => {
    ctx?.dispatch({ type: FlowActionTypes.TOGGLE_PANEL, payload: panel });
  };
  const flowType = ctx?.state.flow?.type;
  let allowProducers = true;
  let showPortsGroup = false;
  if (flowType) {
    const flowSettings = FlowLoader.getFlowTypeSettings(flowType)!;
    if (flowSettings?.allowProducers !== undefined) {
      // eslint-disable-next-line prefer-destructuring
      allowProducers = flowSettings.allowProducers;
    }
    if (flowSettings?.canReturn !== undefined) {
      showPortsGroup = flowSettings.canReturn;
    }
  }

  const panels: { [key: string]: any } = {
    ADD_COMPONENT: {
      title: 'Components',
      icon: 'edit',
      component: (
        <AddFlowComponentPanel isEditable={isEditable} showProducers={allowProducers} showPortsGroup={showPortsGroup} />
      )
    },
    CONFIG_COMPONENT: {
      title: 'Settings',
      icon: 'settings',
      component: <FlowComponentSettings isEditable={isEditable} />
    },
    PORT_SCHEMA: {
      title: 'Port Data',
      icon: 'playCircle',
      component: <PortSchema isEditable={isEditable} />
    },
    JSON_VIEWER: { title: 'JSON Data', icon: 'info', component: <JSONPreview /> },
    FLOW_ERRORS: {
      title: 'Validation',
      icon: 'error',
      component: <FlowErrorsPanel />
    },
    FLOW: {
      title: 'Flow',
      icon: 'start',
      component: <FlowSettings />
    }
  };

  return (
    <>
      <Box
        boxShadow="subtle"
        bg="white"
        style={{
          position: 'absolute',
          right: 0,
          top: 96,
          width: '510px',
          height: 'calc(100vh - 167px',
          zIndex: 100,
          transition: 'transform .3s ease',
          transform: `translateX(${ctx?.state.activePanel ? '0' : '470px'})`,
          paddingRight: '40px'
        }}
      >
        {ctx?.state.activePanel && (
          <ActivePanel panel={panels[ctx?.state.activePanel]} onClose={closePanel} isEditable={isEditable} />
        )}
      </Box>
      <Box
        boxShadow="subtle"
        bg="white"
        style={{
          position: 'absolute',
          right: 0,
          top: 96,
          width: '35px',
          height: 'calc(100vh - 167px',
          zIndex: 200
        }}
      >
        <div style={{ writingMode: 'vertical-lr' }}>
          {mapRecordToArray(panels, (panel, key) => (
            <FoldedPanel key={`panel_${key}`} panel={{ panelKey: key, ...panel }} openPanel={openPanel} />
          ))}
        </div>
      </Box>
    </>
  );
};

export default FlowPanel;

interface IActivePanelProps {
  panel: { title: string; component: JSX.Element };
  isEditable: boolean;
  onClose: () => void;
}

const ActivePanel: FC<IActivePanelProps> = ({ panel, isEditable, onClose }) => (
  <>
    <FlexLayout p="lg" alignItems="center" justifyContent="space-between">
      <H4 fontSize="xl" fontWeight="semiBold">
        {panel.title}
      </H4>
      <Button endIcon={<Icon icon="close" />} onClick={onClose} />
    </FlexLayout>
    <Divider />
    <Box style={{ overflowY: 'scroll', height: 'calc(100% - 110px)' }}>{panel.component}</Box>
  </>
);
